import type { ComponentType } from "react";
import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import type { NearMeConfiguration } from "@/react/api/crmFiltersApi";
import { useCrmFilters } from "@/react/hooks/useCrmFilters";
import { useIsActivityScoreEnabledOrUpgradable } from "@circle-react/hooks/useIsActivityScoreEnabled";
import {
  MemberFilters,
  SharedFilters,
} from "@circle-react-shared/MemberFilters";
import { AllFiltersList } from "@circle-react-shared/MemberFilters/DynamicFilters/AllFiltersList";
import type { RenderFilter } from "@circle-react-shared/MemberFilters/types";
import { NearMe } from "../../shared/MemberFilters/SharedFilters/NearMe";
import { useCrmTrueLocationFieldAndSearchEnabled } from "../../shared/MemberFilters/useCrmLocationEnabled";

const {
  RoleFilter,
  SpaceGroupsFilterV2,
  SpacesFilter,
  StatusFilter,
  MemberTagsFilter,
  PaywallsFilter,
  EventsFilter,
  HasPastDueSubscriptionFilter,
  JoinedCommunityDaysAgoFilter,
  LastLoggedInDaysAgoFilter,
  AvatarSet,
  ActivityScoreFilter,
  NameFilter,
  BioFilter,
  HeadlineFilter,
  LocationFilter,
  InvitationLinksFilter,
  LevelFilter,
  GeoLocationFilter,
} = SharedFilters;

const daysAgoFilters = [
  {
    name: "joined_community_days_ago",
    component: JoinedCommunityDaysAgoFilter,
    label: t(
      "members_directory.header.search.filters.joined_community_days_ago",
    ),
    listLabel: t(
      "members_directory.header.search.filters.joined_community_days_ago_available",
    ),
  },
  {
    name: "last_logged_in_days_ago",
    component: LastLoggedInDaysAgoFilter,
    label: t("members_directory.header.search.filters.last_logged_in_days_ago"),
  },
];

const platformMemberFilters: RenderFilter[] = [
  {
    name: "name",
    component: NameFilter,
    label: t("members_directory.header.search.filters.name"),
  },
  {
    name: "bio",
    component: BioFilter,
    label: t("members_directory.header.search.filters.bio"),
  },
  {
    name: "headline",
    component: HeadlineFilter,
    label: t("members_directory.header.search.filters.headline"),
  },
  {
    name: "geo_location",
    component: GeoLocationFilter,
    label: t("members_directory.header.search.filters.geo_location"),
  },
  {
    name: "location",
    component: LocationFilter,
    label: t("members_directory.header.search.filters.location"),
  },
  {
    name: "role",
    component: RoleFilter,
    label: t("members_directory.header.search.filters.role"),
  },
  {
    name: "invitation_link_ids",
    component: InvitationLinksFilter,
    label: t(
      "members_directory.header.search.filters.invitation_link_ids.singular_title",
    ),
  },
  {
    name: "spaces",
    component: SpacesFilter,
    label: t("members_directory.header.search.filters.spaces.singular_title"),
  },
  {
    name: "space_groups",
    component: SpaceGroupsFilterV2,
    label: t(
      "members_directory.header.search.filters.space_groups.singular_title",
    ),
  },
  {
    name: "online_status",
    component: StatusFilter,
    label: t("members_directory.header.search.filters.online_status"),
  },
  {
    name: "tags",
    component: MemberTagsFilter,
    label: t("members_directory.header.search.filters.tags.singular_title"),
  },
  {
    name: "paywalls",
    component: PaywallsFilter,
    label: t("members_directory.header.search.filters.paywalls.singular_title"),
  },
  {
    name: "events",
    component: EventsFilter,
    label: t("members_directory.header.search.filters.events.singular_title"),
  },
  {
    name: "has_past_due_subscription",
    component: HasPastDueSubscriptionFilter,
    label: t(
      "members_directory.header.search.filters.has_past_due_subscription",
    ),
  },
  ...daysAgoFilters,
  {
    name: "is_avatar_set",
    component: AvatarSet,
    label: t("members_directory.header.search.filters.avatar_set"),
  },
  {
    name: "activity_score",
    component: ActivityScoreFilter,
    label: t("members_directory.header.search.filters.activity_score"),
  },
  {
    name: "level",
    component: LevelFilter,
    label: t("members_directory.header.search.filters.level"),
  },
];

const crmFilterComponents: Record<
  NearMeConfiguration["input_type"],
  ComponentType<any>
> = {
  near_me: NearMe,
};

export const MembersFilters = () => {
  const isActivityScoreEnabledOrUpgradable =
    useIsActivityScoreEnabledOrUpgradable();

  const isCrmTrueLocationFieldAndSearchEnabled =
    useCrmTrueLocationFieldAndSearchEnabled();

  const { data: crmFiltersData } = useCrmFilters();

  const crmFilters = useMemo(() => {
    if (!crmFiltersData?.filter_configurations) {
      return [];
    }

    return crmFiltersData.filter_configurations.reduce(
      (acc: RenderFilter[], filter) => {
        const Component =
          crmFilterComponents[
            filter.input_type as keyof typeof crmFilterComponents
          ];
        if (Component) {
          acc.push({
            name: filter.key,
            component: Component,
            label: filter.label,
            configuration: filter,
          });
        }
        return acc;
      },
      [],
    );
  }, [crmFiltersData]);

  const defaultFilterItems = useMemo(() => {
    const items = ["name", "spaces", "tags", "events"];
    if (isActivityScoreEnabledOrUpgradable) {
      items.push("activity_score");
    }

    if (isCrmTrueLocationFieldAndSearchEnabled) {
      items.unshift("near_me");
    }
    return items;
  }, [
    isActivityScoreEnabledOrUpgradable,
    isCrmTrueLocationFieldAndSearchEnabled,
  ]);

  return (
    // Mobile version of filters are not ready, so hide for time being
    <div className="shadow-xs hidden md:flex">
      <MemberFilters
        platformFilters={[...platformMemberFilters, ...crmFilters]}
        defaultFilterNames={defaultFilterItems}
        persistentFilterNames={
          isCrmTrueLocationFieldAndSearchEnabled ? ["near_me"] : []
        }
      >
        <AllFiltersList />
      </MemberFilters>
    </div>
  );
};
